import type { Dispatch, MutableRefObject, SetStateAction } from 'react';

export type RangeSnapshot = {
  rectKey: string;
  startContainer: globalThis.Node;
  startOffset: number;
  endContainer: globalThis.Node;
  endOffset: number;
};

const normalizeRectValue = (value: unknown): number => {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return 0;
  }
  return Math.round(value);
};

export const getRangeSnapshot = (range: globalThis.Range): RangeSnapshot => {
  const rect =
    typeof range.getBoundingClientRect === 'function'
      ? range.getBoundingClientRect()
      : ({ x: 0, y: 0, width: 0, height: 0, left: 0, top: 0 } as DOMRect);
  const x = normalizeRectValue(rect.x ?? rect.left);
  const y = normalizeRectValue(rect.y ?? rect.top);
  const width = normalizeRectValue(rect.width);
  const height = normalizeRectValue(rect.height);

  return {
    rectKey: `${x}:${y}:${width}:${height}`,
    startContainer: range.startContainer,
    startOffset: range.startOffset,
    endContainer: range.endContainer,
    endOffset: range.endOffset,
  };
};

export const hasRangeSnapshotChanged = (
  previous: Nullable<RangeSnapshot>,
  next: Nullable<RangeSnapshot>
): boolean => {
  if (previous == null || next == null) {
    return previous !== next;
  }

  return (
    previous.rectKey !== next.rectKey ||
    previous.startContainer !== next.startContainer ||
    previous.startOffset !== next.startOffset ||
    previous.endContainer !== next.endContainer ||
    previous.endOffset !== next.endOffset
  );
};

export const setReferenceElementIfRangeChanged = ({
  nextRange,
  previousRangeSnapshotRef,
  setReferenceElement,
}: {
  nextRange: globalThis.Range | null;
  previousRangeSnapshotRef: MutableRefObject<Nullable<RangeSnapshot>>;
  setReferenceElement: Dispatch<SetStateAction<globalThis.Range | null>>;
}): void => {
  const nextSnapshot = nextRange == null ? null : getRangeSnapshot(nextRange);
  if (!hasRangeSnapshotChanged(previousRangeSnapshotRef.current, nextSnapshot)) {
    return;
  }

  previousRangeSnapshotRef.current = nextSnapshot;
  setReferenceElement(nextRange);
};
